import React from 'react';
import { Button, Checkbox, ColorPicker, Input, InputNumber } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
	parseDesignPresetShadows,
	stringifyDesignPresetShadows,
	type DesignPresetShadow,
	type DesignPresetShadowProperty,
} from 'common/design-tokens/shadows';
import css from './DesignPresetsEditor.scss';

/** Resource editor: canonical px, with no Card Editor/iframe state. */
export default function ShadowControl({
	property,
	value,
	disabled,
	onChange,
}: {
	property: DesignPresetShadowProperty;
	value: string;
	disabled?: boolean;
	onChange: (value: string) => void;
}) {
	let shadows: DesignPresetShadow[] | undefined;
	try {
		shadows = parseDesignPresetShadows(property, value);
	} catch {
		/* Keep invalid CSS editable. */
	}
	const update = (index: number, change: Partial<DesignPresetShadow>) => {
		if (shadows)
			onChange(
				stringifyDesignPresetShadows(
					shadows.map((shadow, i) => (i === index ? { ...shadow, ...change } : shadow))
				)
			);
	};
	return (
		<div className={css.shadowControl}>
			{shadows?.map((shadow, index) => (
				// CSS layers are ordered; each field is controlled and holds no per-layer state.
				// eslint-disable-next-line react/no-array-index-key
				<fieldset className={css.shadowLayer} key={index} disabled={disabled}>
					<legend>Shadow {index + 1}</legend>
					<div className={css.shadowLengths}>
						{(['x', 'y', 'blur', ...(property === 'boxShadow' ? (['spread'] as const) : [])] as const).map(
							field => (
								<label key={field}>
									<span>
										{{ x: 'Offset X', y: 'Offset Y', blur: 'Blur', spread: 'Spread' }[field]}
									</span>
									<InputNumber
										aria-label={`Shadow ${index + 1} ${field}`}
										value={shadow[field] ?? 0}
										min={field === 'blur' ? 0 : undefined}
										suffix="px"
										disabled={disabled}
										onChange={next => {
											if (next !== null) update(index, { [field]: next });
										}}
									/>
								</label>
							)
						)}
					</div>
					<div className={css.shadowActions}>
						{property === 'boxShadow' ? (
							<Checkbox
								checked={shadow.inset}
								disabled={disabled}
								onChange={event => update(index, { inset: event.target.checked })}
							>
								Inset
							</Checkbox>
						) : null}
						<ColorPicker
							value={shadow.color}
							disabled={disabled}
							showText={() => (shadow.color ? 'Color' : 'Current text color')}
							onChange={color => update(index, { color: color.toRgbString() })}
						/>
						<Button
							type="text"
							icon={<DeleteOutlined />}
							aria-label={`Remove shadow ${index + 1}`}
							disabled={disabled}
							onClick={() =>
								onChange(stringifyDesignPresetShadows(shadows!.filter((_, i) => i !== index)))
							}
						/>
					</div>
				</fieldset>
			))}
			<Button
				type="text"
				icon={<PlusOutlined />}
				disabled={disabled || !shadows}
				onClick={() =>
					onChange(
						stringifyDesignPresetShadows([
							...(shadows ?? []),
							{
								x: 0,
								y: 2,
								blur: 8,
								...(property === 'boxShadow' ? { spread: 0 } : {}),
								inset: false,
								color: 'rgba(0, 0, 0, 0.25)',
							},
						])
					)
				}
			>
				Add shadow
			</Button>
			<label className={css.shadowCss}>
				<span>CSS value</span>
				<Input.TextArea
					aria-label="Shadow CSS value"
					value={value}
					disabled={disabled}
					autoSize={{ minRows: 2, maxRows: 4 }}
					onChange={event => onChange(event.target.value)}
				/>
			</label>
			<span className={css.muted}>
				{property === 'boxShadow'
					? 'X Y blur spread color · optional inset'
					: 'X Y blur color · no spread or inset'}
				. Use px or 0; separate shadows with commas.
			</span>
		</div>
	);
}
